<template>
  <a
    class="product-card"
    :class="{
      [$class('loading')]: loading,
      'show-secondary-image': showSecondaryImage,
    }"
    :href="formatUrl(product.url)"
    @mouseenter="loadSecondaryImage = true"
  >
    <div class="product-card__image-container">
      <product-badge
        v-if="badge"
        :text="badge"
        :badge-class="productBadgeClass"
        :text-class="productBadgeTextClass"
      />

      <responsive-image
        v-if="loading"
        class="product-card__primary-image"
        min-max="200-500"
        ratio="3:2"
      />

      <responsive-image
        v-else
        class="product-card__primary-image"
        :image="product.featuredImage"
        min-max="200-500"
        ratio="3:2"
      />

      <responsive-image
        v-if="loadSecondaryImage && product.secondaryImage?.src"
        class="product-card__secondary-image"
        :image="product.secondaryImage"
        :padding="false"
        ratio="3:2"
        @image-loaded="showSecondaryImage = true"
      />
      
    </div>

    <div class="product-card__footer">
      <p
        class="
        product-card__title
        text-heading-s
        text-heading-m-desktop
      "
        v-text="product.title"
      />
      <p v-if="product.hasOnlyDefaultVariant"
        class="
          product-card__sku
          text-body-l-regular
        "
        v-text="`${$string('product.general.sku')}: ${product.sku}`"
      />
     
      <p
        v-if="product.compatibility"
        class="
          product-card__compatibility
          text-body-l-regular
        "
      >
        {{ $string('product.general.compatible_with') }}:
        <span v-html="sanitizeCompatibility(product.compatibility)" />
      </p>

      <div class="product-card__actions">
        <product-prices
          :plp="plp"
          class-price="product-card__price text-other-price-small text-other-price-large-desktop"
          class-compare-at-price="text-other-price-small text-other-price-large-desktop"
          :compare-at-price="product?.compareAtPrice?.amount"
          :price="product?.price?.amount"
          :start-price="product?.price?.startPrice"
          :varies="product.price.startPrice &&
            product.price.startPrice !== product?.price?.amount
          "
           :variant= "product.hasOnlyDefaultVariant"
          class-varies="
            text-body-m-regular text-body-l-regular-desktop
          "
        />

        <btn
          v-if="product.hasOnlyDefaultVariant"
          class="product-card__add-to-cart"
          :label="$string('product.cta.add_to_cart')"
          :label-html="$string('product.cta.add_to_cart')"
          modifiers="primary"
          :show-icon="false"
          type="button"
          @click.prevent="handleAddToCart"
        />

        <btn
          v-else
          class="product-card__add-to-cart"
          :label="$string('product.cta.view_product')"
          modifiers="primary"
          :show-icon="false"
          type="button"
          @click.prevent="$emit('display-options')"
        />
      </div>
    </div>
  </a>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import Btn from '~global/btn/btn'
import ProductBadge from '~async/product-badge/product-badge.vue'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    ProductPrices,
    ResponsiveImage,
    ProductBadge,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    loading: {
      type: [Boolean],
      default: false,
      required: true,
    },
    plp:{
      type: [Boolean],
      default: false,
    }
  },

  emits: {
    'display-options': () => true,
    'product-card-loaded': () => true,
  },

  data() {
    return {
      loadSecondaryImage: false,
      showSecondaryImage: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Generate badge class
     */
    productBadgeClass() {
      let badgeClass = ''
      if (!this.badge) {
        return badgeClass
      }

      const badge = this.badge.replace(' ', '-')
      badgeClass = `product-badge--${badge}`
      return badgeClass
    },

    /**
     * Generate badge text
     */
    badge() {
      if (!this.product) {
        return
      }

      let badge = false
      let badgeTag = false
      const { product } = this
      const sale = product.price.amount < product.compareAtPrice.amount

      if (sale) {
        badge = this.$string('collection.badge.sale').toLowerCase()
        return badge
      }

      if (product.badge) {
        badgeTag = product.badge[0]
      } else {
        badgeTag = product.tags ? product.tags.find((tag) => tag.includes('badge:')) : false
      }

      if (badgeTag) {
        badge = badgeTag.replace('badge:', '').toLowerCase()
      }

      return badge
    },

    productBadgeTextClass() {
      return `text-heading-s`
    },
  },

  mounted() {
    this.emitState()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    emitState() {
      this.$emit('product-card-loaded')
    },

    async handleAddToCart() {
      const variantId = this.product.variants ? this.product.variants[0].id : this.product.id

      const items = [
        {
          id: variantId,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

      } catch (error) {
        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    sanitizeCompatibility(values) {
      if (!values) {
        return
      }

      let output = ``
      let truncated = false
      const products = values.split('|')
      const limit = this.getTruncateLimit(this.isMobile)

      products.forEach((product, index) => {

        if (index < limit) {
          output += `<span class="product-card__compatibility-product">${product}</span>`
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!truncated) {
            output += `<span class="product-card__compatibility-product">+${products.length - index}</span>`
            truncated = true
          }
        }
      })

      return output
    },

    getTruncateLimit(isMobile) {
      if (isMobile) {
        return 3
      }
      return 4
    },

    /**
   * Format url to include market handle url.
   * @param {String} url - Url string.
   * @returns {String}
   */
    formatUrl(url) {
      const { origin } = window.cnvs.request
      const fullUrl = url.includes(origin) ? url : origin + url

      const pathname = new URL(fullUrl).pathname
      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const country = cnvs.store.country.toLowerCase()
      const language = cnvs.store.language.toLowerCase()
      const isPrimaryDomain = primary.domain.toLowerCase() === country
      const isPrimaryLocale = primary.locale.toLowerCase() === language
      const isUSPath = country === 'us' && url.includes(`/${language}/`)
      let updatedPathname = `/${language}-${country}${pathname}`

      if (url.includes(`${language}-${country}`) || isUSPath) {
        return fullUrl
      }

      if (isPrimaryDomain) {
        updatedPathname = updatedPathname.replace(`-${country}`, '')

        if (isPrimaryLocale) {
          updatedPathname = updatedPathname.replace(`/${language}`, '')
        }
      }

      return updatedPathname
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
